<template>
  <div class="channels-configuration-container">
    <div v-if="!showChannelForm">
      <mf-card-container title="Configurações da campanha">
        <div class="channels-header">
          <div class="title-description-container">
            <div class="title-container">
              <strong class="text-cinza-l20">Título</strong>
              <mf-tooltip :disabled="isTitleTooShort" max-width="250" top>
                <template #activator="{ on }">
                  <span class="text-cinza-d10" v-on="on">{{ campaign.title | truncate({ length: 62 }) }}</span>
                </template>
                <span>{{ campaign.title }}</span>
              </mf-tooltip>
            </div>
            <div class="description-container">
              <strong class="text-cinza-l20">Descrição</strong>
              <mf-tooltip :disabled="isDescriptionTooShort" max-width="250" top>
                <template #activator="{ on }">
                  <span class="text-cinza-d10" v-on="on">{{ campaign.description | truncate({ length: 80 }) }}</span>
                </template>
                <span>{{ campaign.description }}</span>
              </mf-tooltip>
            </div>
          </div>
          <v-divider />
          <select-units ref="selectUnits" :disable-fields="disabled" />
          <v-divider />
          <campaign-period ref="campaignPeriod" :disabled="disabled" />
        </div>
      </mf-card-container>
      <dispatch-model-and-product ref="dispatchModelAndProduct" :disabled="disabled" />
      <message-dispatch-config ref="messageDispatchConfig" class="mt-10" />
    </div>

    <channel-settings-form
      v-else
      :selected-channel="selectedChannel"
      :settings-id="settingsId"
      :is-editing-channel="isEditingChannel"
      @close="onCancelChannelForm"
      @channel-saved="onChannelSaved"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { mariaCampaign } from '@/modules/maria/campanha-da-maria-v2/mixins/maria-campaign'
import { charCount } from '@/modules/maria/campanha-da-maria-v2/mixins/char-count'
import CampaignPeriod from './CampaignPeriod.vue'
import SelectUnits from './SelectUnits.vue'
import DispatchModelAndProduct from './DispatchModelAndProduct.vue'
import ChannelSettingsForm from './ChannelSettingsForm'
import MessageDispatchConfig from './MessageDispatchConfig.vue'

export default {
  name: 'ChannelsConfiguration',
  components: {
    CampaignPeriod,
    SelectUnits,
    DispatchModelAndProduct,
    MessageDispatchConfig,
    ChannelSettingsForm,
  },
  mixins: [mariaCampaign, charCount],
  data: () => ({
    showChannelForm: false,
    selectedChannel: '',
    settingsId: '',
    isEditingChannel: false,
  }),
  computed: {
    ...mapGetters(['campaignVariablesConfiguration', 'previousOfferSchedules']),
    isTitleTooShort() {
      return this.campaign.title.length < 62
    },
    isDescriptionTooShort() {
      return this.campaign.description.length < 80
    },
    disabled() {
      return this.isMariaCampaignFinished || this.isMariaCampaignActive
    },
  },
  watch: {
    selectedChannel(newChannel) {
      this.$emit('channel-selected', newChannel)
    },
  },
  async mounted() {
    await this.getCampaignVariablesConfiguration({
      variableType: 'BEST_OFFER',
      id: this.campaignVariableConfigurationId ?? null,
    })
    this.countNameVariablesChar()
    this.countComboNameChar()
    this.countBestOfferTextChar()

    this.$bus.$on('set-selected-channel', ({ channelName, settingsId, isEditing }) => {
      this.setSelectedChannel(channelName, settingsId, isEditing)
    })

    this.setDate()
    this.setUnits()
  },
  methods: {
    ...mapActions(['getDaysOnOffers', 'getCampaignVariablesConfiguration', 'setPreviousOfferSchedules']),
    validateStepFourFormData() {
      const isValid = [this.$refs.selectUnits, this.$refs.campaignPeriod, this.$refs.dispatchModelAndProduct, this.$refs.messageDispatchConfig]
        .map((ref) => ref.validate())
        .every((valid) => valid)
      if (!isValid) {
        this.$snackbar({ message: 'Preencha todos os campos corretamente.', snackbarColor: 'error' })
        this.goToErrorField()
      }
      return isValid
    },
    setSelectedChannel(channelName, settingsId, isEditing) {
      this.selectedChannel = channelName
      this.settingsId = settingsId
      this.isEditingChannel = isEditing
      this.showChannelForm = true
    },
    onCancelChannelForm() {
      this.showChannelForm = false
      this.selectedChannel = ''
    },
    setUnits() {
      try {
        let units = []
        let unitsConfiguration = []

        if (this.campaign.units.length) {
          units = this.campaign.units.map((unit) => unit.id_erp ?? unit)
          unitsConfiguration = this.campaign.units.map((unit) => {
            return { value: unit.id_erp, dataType: unit.dataType }
          })
        }

        this.setOmniCampaignKey({ key: 'units', payload: units })
        this.setOmniCampaignKey({ key: 'units_configuration', payload: unitsConfiguration })

        if (this.campaignHasOffers) {
          const currentOfferScheduleIds = this.campaign.offer_schedules.map(
            (schedule) => schedule.offer_schedule?._id || schedule?.offer_schedule_id || schedule?._id
          )
          const hasOfferSchedulesChanged = units.length === 0 || JSON.stringify(currentOfferScheduleIds) !== JSON.stringify(this.previousOfferSchedules)

          if (hasOfferSchedulesChanged) {
            this.setOmniCampaignKey({ key: 'units', payload: this.unitsFromOfferSchedules })
            this.setPreviousOfferSchedules(this.campaign.offer_schedules)
          }
        }
      } catch (error) {
        console.error('Error setting units', error)
      }
    },
    setDate() {
      try {
        let latestEndDate = this.$refs.campaignPeriod.findLatestEndDate()
        latestEndDate = latestEndDate ? latestEndDate.toFormat('yyyy-MM-dd') : ''
        if (!this.$refs.campaignPeriod.initialDate && !this.$refs.campaignPeriod.finalDate) {
          this.$refs.campaignPeriod.setDateValue()
        } else if (this.$refs.campaignPeriod.finalDate !== latestEndDate) {
          this.$refs.campaignPeriod.setOmniCampaignKey({ key: 'final_date', payload: latestEndDate })
        }
      } catch (error) {
        console.error('Error setting date', error)
      }
    },
    onChannelSaved() {
      this.$nextTick(() => {
        if (this.$refs.messageDispatchConfig) {
          this.$refs.messageDispatchConfig.focus(this.settingsId)
        }
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.channels-configuration-container {
  font-family: Lato; /* Remover !important */
}

.channels-header {
  display: flex;
  flex-direction: column;
  gap: 20px;
  font-size: 14px;
  line-height: 20px;

  .title-description-container {
    margin-top: 20px;
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 20px;

    .title-container,
    .description-container {
      display: flex;
      flex-direction: column;
      gap: 4px;
      min-width: 300px;
    }
  }
}

.text-cinza-d10 {
  color: $cinza-d10;
}

.text-cinza-l20 {
  color: $cinza-l20;
}
</style>
